'use client';

import { FC, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FormDatePicker } from '@/components/common/FormComponents';
import { StandardDialog } from '@/components/common/StandardDialog';
import { OptionKey } from '@/lib/options/optionTypes';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { useBackendStore } from '@/stores/backendStore';
import { useSnackbar } from '@/hooks/useSnackbar';
import {
  PrivilegeEnum,
  ProjectDto,
  ReportingCategoryDto,
  SampleDto,
  SampleProjectMultiplicityPolicy,
  StatusDto,
} from '@/api/ApiClient';
import { useAuthStore } from '@/stores/authStore';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { EntityAutocompleteWithLookup } from '@/components/common/EntityAutocompleteWithLookup';
import { useDialogStore } from '@/stores/dialogStore';
import { LABEL_SUFFIX_OPTIONAL, LABEL_SUFFIX_REQUIRED } from '@/lib/formLabels';
import { mayClearAllProjects } from '@/lib/auth/unassignedSampleAccess';
import { useUserInteraction } from '@/hooks/useUnsavedChanges';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import {
  createProjectResolver,
  enforceProjectMultiplicitySelection,
  sampleProjectMultiplicityFieldHint,
  validateProjectIdsForSampleMultiplicity,
} from '@/lib/utils/sampleProjectMultiplicity';
import {
  applyResearchCategoryAndTransfer,
  isTransferReportingCategoryForbiddenError,
  planReportingCategoryTransfer,
  transferSampleOnly,
  TRANSFER_REPORTING_CATEGORY_DIALOG_TITLE,
  TRANSFER_REPORTING_CATEGORY_EXPLANATION,
  validateTransferSubmitPreflightWithBackend,
} from '@/lib/utils/sampleTransferPreflight';
import { usePrivileges } from '@/hooks/usePrivileges';

const OPTION_KEYS: OptionKey[] = ['projects', 'projectType', 'sampleReportingCategory'];

interface AddTransferActivityDialogProps {
  open: boolean;
  onClose: () => void;
  sample: SampleDto;
  isCurrentProjectInHouse?: boolean;
  onSubmit?: (activityIds: number[]) => void;
}

interface AddTransferActivityDialogInnerProps extends AddTransferActivityDialogProps {
  currentProjectLabels: string[];
  targetProjectOptions: { id: number; label: string }[];
  canClearAllProjects: boolean;
  multiplicityPolicy: SampleProjectMultiplicityPolicy | undefined;
  resolveProjectForPolicy: (id: number) => ProjectDto | undefined;
  reportingCategories: { id?: number; code?: string; label?: string; requiresInternalSamplePrivilege?: boolean }[];
  canUpdateSample: boolean;
  hasGlobalTransferCreate: boolean;
}

const AddTransferActivityDialogInner: FC<AddTransferActivityDialogInnerProps> = ({
  open,
  onClose,
  sample,
  isCurrentProjectInHouse,
  onSubmit: _onSubmit,
  currentProjectLabels,
  targetProjectOptions,
  canClearAllProjects,
  multiplicityPolicy,
  resolveProjectForPolicy,
  reportingCategories,
  canUpdateSample,
  hasGlobalTransferCreate,
}) => {
  const backend = useBackendStore((state) => state.getBackend());
  const { showSnackbar } = useSnackbar();
  const { showProjectSelectionDialog } = useDialogStore();
  const multiplicityHint = sampleProjectMultiplicityFieldHint(multiplicityPolicy);
  const allowMultipleTargets = multiplicityPolicy !== 'MaxOneProject';

  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedTargetProjects, setSelectedTargetProjects] = useState<{ id: number; label: string }[]>([]);
  const [createActivity, setCreateActivity] = useState<boolean>(true);
  const [projectError, setProjectError] = useState(false);
  const [projectMultiplicityMessage, setProjectMultiplicityMessage] = useState<string | null>(null);
  const [reportingConfirmMessage, setReportingConfirmMessage] = useState<string | null>(null);
  const [pendingResearchCategory, setPendingResearchCategory] = useState<ReportingCategoryDto | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { hasUnsavedChanges, markAsInteracted } = useUserInteraction();

  const resetFormState = () => {
    setSelectedDate(new Date());
    setSelectedTargetProjects([]);
    setCreateActivity(true);
    setProjectError(false);
    setProjectMultiplicityMessage(null);
    setReportingConfirmMessage(null);
    setPendingResearchCategory(null);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetFormState();
    onClose();
  };

  const finishTransferSuccess = (targetProjectLabels: string[]) => {
    showSnackbar(
      targetProjectLabels.length > 0
        ? `Sample transferred to ${targetProjectLabels.join(', ')}.`
        : 'Sample removed from all projects.',
      'success'
    );
    handleClose();
  };

  const runTransfer = async (targetProjectLabels: string[], withoutActivity: boolean, targetProjects: ProjectDto[]) => {
    const executeDeps = {
      backend,
      sample,
      targetProjectLabels,
      withoutActivity,
    };

    const plan = planReportingCategoryTransfer({
      sample,
      sourceProjectIds: sample.projectIds ?? [],
      targetProjects,
      reportingCategories,
      resolveProject: resolveProjectForPolicy,
      canUpdateSample,
    });

    switch (plan.kind) {
      case 'transfer-only':
        await transferSampleOnly(executeDeps);
        finishTransferSuccess(targetProjectLabels);
        return;
      case 'blocked':
        showSnackbar(plan.message, 'error');
        return;
      case 'confirm-then-transfer':
        setReportingConfirmMessage(plan.confirm.message);
        setPendingResearchCategory(plan.researchCategory);
        return;
    }
  };

  const handleConfirmReportingCategory = async () => {
    if (!pendingResearchCategory) {
      return;
    }

    const targetProjectLabels = selectedTargetProjects.map((p) => p.label);
    const withoutActivity = !createActivity;

    setIsSubmitting(true);
    try {
      const preflight = await validateTransferSubmitPreflightWithBackend(
        selectedTargetProjects,
        sample.sampleLabel ?? '',
        sample.projectIds ?? [],
        hasGlobalTransferCreate,
        backend
      );
      if (preflight.kind === 'forbidden') {
        showSnackbar(preflight.message, 'error');
        return;
      }

      await applyResearchCategoryAndTransfer({
        backend,
        sample,
        targetProjectLabels,
        withoutActivity,
        researchCategory: pendingResearchCategory,
      });
      finishTransferSuccess(targetProjectLabels);
    } catch (error: unknown) {
      if (isTransferReportingCategoryForbiddenError(error)) {
        showSnackbar(TRANSFER_REPORTING_CATEGORY_EXPLANATION, 'error', 6000, TRANSFER_REPORTING_CATEGORY_DIALOG_TITLE);
        return;
      }
      showSnackbar(getProblemDetail(error) ?? 'Failed to create transfer activity.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProjectLookup = async () => {
    const projectsResult = await showProjectSelectionDialog(
      allowMultipleTargets ? 'Select Target Projects' : 'Select Target Project',
      { allowMultiple: allowMultipleTargets }
    );
    if (projectsResult) {
      const newProjects = projectsResult.map((p) => ({ id: p.id!, label: p.projectLabel! }));
      const { nextProjects, multiplicityMessage } = enforceProjectMultiplicitySelection(
        multiplicityPolicy,
        newProjects,
        resolveProjectForPolicy
      );
      setSelectedTargetProjects(nextProjects);
      setProjectMultiplicityMessage(multiplicityMessage);
      markAsInteracted();
    }
  };

  const handleSubmit = async () => {
    let hasError = false;

    if (selectedTargetProjects.length === 0) {
      if (!canClearAllProjects) {
        setProjectError(true);
        hasError = true;
      } else {
        setProjectError(false);
      }
    } else {
      setProjectError(false);
    }

    if (hasError) {
      showSnackbar(
        selectedTargetProjects.length === 0 && !canClearAllProjects
          ? 'You are not authorized to remove all projects from this sample.'
          : 'Please fill out all required fields.',
        'error'
      );
      return;
    }

    const multErr = validateProjectIdsForSampleMultiplicity(
      multiplicityPolicy,
      selectedTargetProjects.map((p) => p.id),
      resolveProjectForPolicy
    );
    if (multErr) {
      showSnackbar(multErr, 'error');
      return;
    }

    const targetProjectLabels = selectedTargetProjects.map((p) => p.label);
    const withoutActivity = !createActivity;
    const targetProjects = selectedTargetProjects
      .map((p) => resolveProjectForPolicy(p.id))
      .filter((p): p is ProjectDto => p != null);

    setIsSubmitting(true);
    try {
      const preflight = await validateTransferSubmitPreflightWithBackend(
        selectedTargetProjects,
        sample.sampleLabel ?? '',
        sample.projectIds ?? [],
        hasGlobalTransferCreate,
        backend
      );
      if (preflight.kind === 'forbidden') {
        showSnackbar(preflight.message, 'error');
        return;
      }

      await runTransfer(targetProjectLabels, withoutActivity, targetProjects);
    } catch (error: unknown) {
      if (isTransferReportingCategoryForbiddenError(error)) {
        showSnackbar(TRANSFER_REPORTING_CATEGORY_EXPLANATION, 'error', 6000, TRANSFER_REPORTING_CATEGORY_DIALOG_TITLE);
        return;
      }
      showSnackbar(getProblemDetail(error) ?? 'Failed to create transfer activity.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isReportingConfirmStep = reportingConfirmMessage != null;

  return (
    <>
      <StandardDialog
        open={open}
        onClose={handleClose}
        title={isReportingConfirmStep ? TRANSFER_REPORTING_CATEGORY_DIALOG_TITLE : 'Add Transfer Activity'}
        maxWidth="md"
        onSubmit={isReportingConfirmStep ? undefined : handleSubmit}
        submitText="Add Transfer Activity"
        showDefaultActions={!isReportingConfirmStep}
        isLoading={isSubmitting}
        loadingText="Transferring..."
        hasUnsavedChanges={isReportingConfirmStep ? false : hasUnsavedChanges}
        unsavedChangesMessage="You have unsaved changes. Are you sure you want to discard them?"
        actions={
          isReportingConfirmStep ? (
            <>
              <Button
                variant="text"
                disabled={isSubmitting}
                onClick={() => {
                  setReportingConfirmMessage(null);
                  setPendingResearchCategory(null);
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                disabled={isSubmitting}
                onClick={handleConfirmReportingCategory}
                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined}
              >
                {isSubmitting ? 'Transferring...' : 'Change category and transfer'}
              </Button>
            </>
          ) : undefined
        }
      >
        {isReportingConfirmStep ? (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {reportingConfirmMessage}
          </Typography>
        ) : (
          <>
            <Box mb={1}>
              <Typography variant="body2" color="text.secondary">
                {currentProjectLabels.length > 0
                  ? `Current projects: ${currentProjectLabels.join(', ')}`
                  : 'Current projects: none (sample is not assigned to any project)'}
              </Typography>
            </Box>

            <Box display="flex" gap={2}>
              <Box flex={1}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <FormDatePicker
                    label="Date"
                    value={selectedDate}
                    onChange={(newValue) => {
                      setSelectedDate(newValue);
                      markAsInteracted();
                    }}
                    required
                  />
                </LocalizationProvider>
              </Box>
            </Box>

            <Divider />

            <EntityAutocompleteWithLookup
              label={`Target ${allowMultipleTargets ? 'Projects' : 'Project'}${canClearAllProjects ? LABEL_SUFFIX_OPTIONAL : LABEL_SUFFIX_REQUIRED}`}
              options={targetProjectOptions}
              value={selectedTargetProjects}
              onChange={(newValue) => {
                // Policy can make it a single select field, or a multi-select field.
                // We need to handle both cases.
                const incomingProjects = Array.isArray(newValue) ? newValue : newValue != null ? [newValue] : [];
                const { nextProjects, multiplicityMessage } = enforceProjectMultiplicitySelection(
                  multiplicityPolicy,
                  incomingProjects,
                  resolveProjectForPolicy
                );
                setSelectedTargetProjects(nextProjects);
                if (nextProjects.length > 0) {
                  setProjectError(false);
                }
                setProjectMultiplicityMessage(multiplicityMessage);
                markAsInteracted();
              }}
              onLookupClick={handleProjectLookup}
              multiple={allowMultipleTargets}
              error={projectError}
              helperText={
                projectError
                  ? 'You are not authorized to remove all projects from this sample.'
                  : (projectMultiplicityMessage ??
                    multiplicityHint ??
                    (canClearAllProjects ? 'Leave empty to remove from all projects.' : undefined))
              }
              lookupTooltip={allowMultipleTargets ? 'Search and select projects' : 'Search and select a project'}
              fullWidth
            />

            {isCurrentProjectInHouse !== false ? (
              <Box display="flex" alignItems="center">
                <input
                  type="checkbox"
                  id="createActivity"
                  checked={createActivity}
                  onChange={(e) => {
                    setCreateActivity(e.target.checked);
                    markAsInteracted();
                  }}
                  style={{ marginRight: '8px' }}
                />
                <label htmlFor="createActivity">Create transfer activity record</label>
              </Box>
            ) : null}
          </>
        )}
      </StandardDialog>
    </>
  );
};

const AddTransferActivityDialog: FC<AddTransferActivityDialogProps> = ({
  open,
  onClose,
  sample,
  isCurrentProjectInHouse,
  onSubmit,
}) => {
  const { options, loading: optionsLoading } = useSelectOptions(OPTION_KEYS);
  const isAuthorized = useAuthStore((s) => s.isAuthorized);
  const { currentUser } = useAuthStore();
  // Tree sample may omit canUpdateSample; then load getAccessRights once the dialog is open.
  const needsSampleUpdateRights = sample.canUpdateSample === undefined;
  const { isAuthorized: isAuthorizedForSample } = usePrivileges(
    needsSampleUpdateRights ? 'sample' : undefined,
    needsSampleUpdateRights && sample.id != null ? String(sample.id) : undefined
  );
  const canClearAllProjects = mayClearAllProjects(isAuthorized, sample.userId, currentUser);
  const canUpdateSample = sample.canUpdateSample ?? isAuthorizedForSample(PrivilegeEnum.Sample, 'update');
  const hasGlobalTransferCreate = isAuthorized(PrivilegeEnum.TransferActivity, 'create');
  const reportingCategories = options.sampleReportingCategory ?? [];
  const systemConfig = useSystemConfig();
  const multiplicityPolicy = systemConfig?.sampleProjectMultiplicity;

  const projects = useMemo(() => options.projects ?? [], [options.projects]);

  const resolveProjectForPolicy = useMemo(
    () => createProjectResolver(projects, options.projectType),
    [projects, options.projectType]
  );

  const currentProjectLabels = useMemo(() => {
    const ids = sample?.projectIds ?? [];
    if (ids.length === 0 || !projects.length) return [];
    return projects.filter((p) => p.id && ids.includes(p.id)).map((p) => p.projectLabel!);
  }, [projects, sample.projectIds]);

  const targetProjectOptions = useMemo(() => {
    const ids = sample?.projectIds ?? [];
    return projects
      .filter((p) => {
        if (!p.id || !p.projectLabel || p.status !== StatusDto.Submitted) return false;
        if (ids.length === 0) return true;
        return !ids.includes(p.id);
      })
      .map((p) => ({ id: p.id!, label: p.projectLabel! }));
  }, [projects, sample.projectIds]);

  const dialogInitKey = `${sample?.id ?? 'sample'}|${(sample?.projectIds ?? []).join(',')}|${currentUser?.id ?? 'anon'}|${currentUser?.selectedRole ?? ''}|${String(currentUser?.usingSignInAs ?? false)}`;

  if (!sample) {
    return (
      <StandardDialog
        open={open}
        onClose={onClose}
        title="Add Transfer Activity"
        maxWidth="md"
        onSubmit={() => {}}
        submitText="Add Transfer Activity"
        submitDisabled
      >
        <Box sx={{ p: 2 }}>No sample selected.</Box>
      </StandardDialog>
    );
  }

  if (optionsLoading) {
    return (
      <StandardDialog
        open={open}
        onClose={onClose}
        title="Add Transfer Activity"
        maxWidth="md"
        onSubmit={() => {}}
        submitText="Add Transfer Activity"
        submitDisabled
      >
        <Box sx={{ p: 2 }}>Loading options...</Box>
      </StandardDialog>
    );
  }

  return (
    <AddTransferActivityDialogInner
      key={dialogInitKey}
      open={open}
      onClose={onClose}
      sample={sample}
      isCurrentProjectInHouse={isCurrentProjectInHouse}
      onSubmit={onSubmit}
      currentProjectLabels={currentProjectLabels}
      targetProjectOptions={targetProjectOptions}
      canClearAllProjects={canClearAllProjects}
      multiplicityPolicy={multiplicityPolicy}
      resolveProjectForPolicy={resolveProjectForPolicy}
      reportingCategories={reportingCategories}
      canUpdateSample={canUpdateSample}
      hasGlobalTransferCreate={hasGlobalTransferCreate}
    />
  );
};

export { AddTransferActivityDialog };
